import { ArrowDown, ArrowUp, Download, Edit2, Search } from "lucide-react";
import { useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import type { ComplianceDocument } from "@/hooks/use-compliance";
import { downloadCsv } from "@/lib/csv";
import { cn } from "@/lib/utils";

type SortDir = "asc" | "desc";
type SortField = "companyName" | "documentType" | "status" | "expiryDate" | "notes";

interface ColumnDef {
  field: SortField;
  label: string;
  className?: string;
  hidden?: boolean;
}

function statusVariant(
  status: string | null,
): "success" | "warning" | "destructive" | "secondary" | "default" {
  switch (status) {
    case "active":
    case "compliant":
    case "valid":
    case "current":
      return "success";
    case "pending":
    case "expiring_soon":
      return "warning";
    case "expired":
    case "missing":
    case "non_compliant":
      return "destructive";
    case "not_required":
      return "secondary";
    default:
      return "default";
  }
}

function formatDate(date: string | null): string {
  if (!date) return "--";
  return new Date(date).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function titleCase(value: string | null): string {
  if (!value) return "--";
  return value
    .split(/[_\s]+/)
    .map((w) => (w.length > 0 ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : ""))
    .join(" ");
}

function compareValues(a: unknown, b: unknown): number {
  const aEmpty = a === null || a === undefined || a === "";
  const bEmpty = b === null || b === undefined || b === "";
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1;
  if (bEmpty) return -1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b), undefined, { sensitivity: "base" });
}

function sortFieldValue(doc: ComplianceDocument, field: SortField): unknown {
  switch (field) {
    case "companyName":
      return doc.companyName ?? "";
    case "documentType":
      return doc.documentType ?? "";
    case "status":
      return doc.status ?? "";
    case "expiryDate":
      return doc.expiryDate ?? "";
    case "notes":
      return doc.notes ?? "";
  }
}

interface ComplianceDocumentsTableProps {
  documents: ComplianceDocument[];
  /** When true, hides the Company column (used inside a company detail tab). */
  hideCompanyColumn?: boolean;
  onEdit: (doc: ComplianceDocument) => void;
  csvFilename?: string;
}

export function ComplianceDocumentsTable({
  documents,
  hideCompanyColumn = false,
  onEdit,
  csvFilename = "compliance-documents",
}: ComplianceDocumentsTableProps) {
  const columns: ColumnDef[] = [
    { field: "companyName", label: "Company", hidden: hideCompanyColumn },
    { field: "documentType", label: "Document Type" },
    { field: "status", label: "Status" },
    { field: "expiryDate", label: "Expiry" },
    { field: "notes", label: "Notes" },
  ];
  const visibleColumns = columns.filter((c) => !c.hidden);
  const firstSortField = visibleColumns[0]?.field ?? "documentType";

  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState<SortField>(firstSortField);
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  const [userSorted, setUserSorted] = useState(false);

  const filtered = useMemo(() => {
    if (!search.trim()) return documents;
    const q = search.toLowerCase();
    return documents.filter((doc) => {
      return (
        (doc.companyName ?? "").toLowerCase().includes(q) ||
        (doc.documentType ?? "").toLowerCase().includes(q) ||
        (doc.status ?? "").toLowerCase().includes(q) ||
        (doc.expiryDate ?? "").toLowerCase().includes(q) ||
        (doc.notes ?? "").toLowerCase().includes(q)
      );
    });
  }, [documents, search]);

  const sorted = useMemo(() => {
    const rows = [...filtered];
    rows.sort((a, b) => {
      const cmp = compareValues(sortFieldValue(a, sortField), sortFieldValue(b, sortField));
      return sortDir === "asc" ? cmp : -cmp;
    });
    return rows;
  }, [filtered, sortField, sortDir]);

  const handleSort = (field: SortField) => {
    setUserSorted(true);
    if (sortField === field) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  const handleExport = () => {
    const rows = sorted.map((d) => ({
      company: d.companyName ?? d.companyId,
      documentType: titleCase(d.documentType),
      status: titleCase(d.status),
      expiryDate: d.expiryDate ?? "",
      issueDate: d.issueDate ?? "",
      fileName: d.fileName ?? "",
      notes: d.notes ?? "",
    }));
    const headers: { key: keyof (typeof rows)[number]; label: string }[] = hideCompanyColumn
      ? [
          { key: "documentType", label: "Document Type" },
          { key: "status", label: "Status" },
          { key: "expiryDate", label: "Expiry" },
          { key: "issueDate", label: "Issue Date" },
          { key: "fileName", label: "File" },
          { key: "notes", label: "Notes" },
        ]
      : [
          { key: "company", label: "Company" },
          { key: "documentType", label: "Document Type" },
          { key: "status", label: "Status" },
          { key: "expiryDate", label: "Expiry" },
          { key: "issueDate", label: "Issue Date" },
          { key: "fileName", label: "File" },
          { key: "notes", label: "Notes" },
        ];
    downloadCsv(csvFilename, rows, headers);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div className="relative w-full max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Button variant="outline" size="sm" onClick={handleExport} disabled={sorted.length === 0}>
          <Download className="mr-2 h-4 w-4" />
          Download CSV
        </Button>
      </div>

      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/50">
              {visibleColumns.map((col) => {
                const active = userSorted && sortField === col.field;
                return (
                  <th
                    key={col.field}
                    className={cn(
                      "px-4 py-3 text-left text-xs font-medium text-muted-foreground",
                      col.className,
                    )}
                  >
                    <button
                      type="button"
                      onClick={() => handleSort(col.field)}
                      className="inline-flex items-center gap-1 hover:text-foreground"
                    >
                      <span>{col.label}</span>
                      {active ? (
                        sortDir === "asc" ? (
                          <ArrowUp className="h-3 w-3" />
                        ) : (
                          <ArrowDown className="h-3 w-3" />
                        )
                      ) : null}
                    </button>
                  </th>
                );
              })}
              <th className="w-16 px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 ? (
              <tr>
                <td
                  colSpan={visibleColumns.length + 1}
                  className="px-4 py-8 text-center text-muted-foreground"
                >
                  No compliance documents match your filters.
                </td>
              </tr>
            ) : (
              sorted.map((doc) => (
                <tr
                  key={doc.id}
                  className="border-b last:border-b-0 hover:bg-muted/30 transition-colors cursor-pointer"
                  onDoubleClick={() => onEdit(doc)}
                >
                  {!hideCompanyColumn && (
                    <td className="px-4 py-3 font-medium">{doc.companyName ?? "--"}</td>
                  )}
                  <td className="px-4 py-3">{titleCase(doc.documentType)}</td>
                  <td className="px-4 py-3">
                    <Badge variant={statusVariant(doc.status)}>{titleCase(doc.status)}</Badge>
                  </td>
                  <td className="px-4 py-3">{formatDate(doc.expiryDate)}</td>
                  <td className="px-4 py-3 max-w-[240px] truncate text-muted-foreground">
                    {doc.notes ?? "--"}
                  </td>
                  <td className="px-4 py-3">
                    <Button variant="ghost" size="icon" onClick={() => onEdit(doc)}>
                      <Edit2 className="h-4 w-4" />
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
